const COMPANY_STAFF_SESSION_KEY = 'companyStaffSession';

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const ROLE_LABELS = {
  company_admin: 'Admin',
  company_manager: 'Manager',
  company_staff: 'Staff',
  host: 'Host',
};

const sanitize = (raw = {}, { requireMobile = true } = {}) => {
  const staffProfile = {
    name: String(raw?.name || '').trim(),
    role: String(raw?.role || '').trim(),
    mobileNumber: String(raw?.mobileNumber || raw?.phone || raw?.phoneNumber || '').trim(),
    employeeId: String(raw?.employeeId || '').trim(),
  };

  if (!staffProfile.name || !staffProfile.role) {
    return null;
  }

  if (requireMobile && !staffProfile.mobileNumber) {
    return null;
  }

  if (!staffProfile.mobileNumber) {
    staffProfile.mobileNumber = 'Not provided';
  }

  return staffProfile;
};

export const getCompanyStaffSession = () => {
  if (typeof window === 'undefined') return null;
  const parsed = safeParse(localStorage.getItem(COMPANY_STAFF_SESSION_KEY) || '');
  return sanitize(parsed);
};

export const saveCompanyStaffSession = (staffProfile) => {
  if (typeof window === 'undefined') return null;
  const sanitized = sanitize(staffProfile);
  if (!sanitized) return null;
  localStorage.setItem(COMPANY_STAFF_SESSION_KEY, JSON.stringify(sanitized));
  return sanitized;
};

export const clearCompanyStaffSession = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(COMPANY_STAFF_SESSION_KEY);
};

export const mergeUserWithStaffSession = (user) => {
  if (!user || typeof user !== 'object') return user;
  const staff = getCompanyStaffSession();
  if (!staff) return user;
  return {
    ...user,
    staffProfile: user.staffProfile || staff,
  };
};

export const getStaffPayloadFromUser = (user) => {
  if (!user || typeof user !== 'object') return getCompanyStaffSession();
  return sanitize(user.staffProfile || getCompanyStaffSession());
};

export const mapUserRoleLabel = (role) => ROLE_LABELS[String(role || '').trim()] || String(role || 'Staff').trim();

export const buildStaffDetailsFromUser = (user, overrides = {}) => {
  if (!user || typeof user !== 'object') return null;
  return sanitize(
    {
      name: overrides.name || user.name || user.email?.split('@')[0] || '',
      role: overrides.role || mapUserRoleLabel(user.role),
      mobileNumber:
        overrides.mobileNumber ||
        overrides.phone ||
        user.phoneNumber ||
        user.phone ||
        user.staffProfile?.mobileNumber ||
        '',
      employeeId: overrides.employeeId || user.staffProfile?.employeeId || '',
    },
    { requireMobile: false }
  );
};

/** Resolve staff identity for company bookings (session → profile → overrides). */
export const resolveStaffDetailsForBooking = (user, overrides = {}) => {
  const fromSession = getStaffPayloadFromUser(user);
  if (fromSession) {
    if (overrides.mobileNumber || overrides.phone) {
      return sanitize({ ...fromSession, ...overrides }, { requireMobile: false });
    }
    return fromSession;
  }

  const built = buildStaffDetailsFromUser(user, overrides);
  if (built) {
    saveCompanyStaffSession(built);
  }
  return built;
};

export const isCompanyWorkspaceUser = (user) =>
  Boolean(user?.company?._id || user?.company || user?.companyId);
